import { DateTime, Interval } from 'luxon';
import { nanoid } from 'nanoid';
import { assign, fromPromise, setup } from 'xstate';
import { AllocationPhase, AllocationPhaseUpdate, PhaseColor, PhaseTypeDatabase } from '../Timeline.types';
import { SpecializationType } from './SpecializationMappings';
import { calculateAllocationForm } from './allocationForm';
import { AllocationUsage } from './types';

export type PhaseGroup = {
    id: string;
    startDateTime: DateTime;
    endDateTime: DateTime;
    temporaryCellSpanBefore: number;
    confirmedCellSpan: number;
    confirmedVisibleCellSpan: number;
    temporaryCellSpanAfter: number;
    isLeftEndPosition: boolean;
    isRightEndPosition: boolean;
    insertPhaseBefore: string | null; // string === Name of phase, null === Button is disabled
    insertPhaseAfter: string | null; // string === Name of phase, null === Button is disabled, only relevant for very last phase
    position: 'Before-Main' | 'Main' | 'After-Main';
    temporarySpecialization: SpecializationType | null;
    confirmedSpecialization: SpecializationType | null;
    isChangeTypePopupOpen: boolean;
    color: PhaseColor;
    usage: {
        id: string;
        position: number;
        type: PhaseTypeDatabase;
    };
};

// Calculates the visible cell spans within the timeline range so that allocations which are longer than the displayed range are cut off
function calculateConfirmedVisibleCellSpan(
    phaseGroupStartDateTime: DateTime,
    phaseGroupEndDateTime: DateTime,
    timelineStartDateTime: DateTime,
    timelineEndDateTime: DateTime,
): number {
    const phaseInterval = Interval.fromDateTimes(phaseGroupStartDateTime, phaseGroupEndDateTime);
    const timelineInterval = Interval.fromDateTimes(timelineStartDateTime, timelineEndDateTime);

    if (!phaseInterval.isValid || !timelineInterval.isValid) {
        return 0;
    }

    const intersectionInterval = phaseInterval.intersection(timelineInterval);

    return intersectionInterval?.count('days') ?? 0;
}

function enrichPhases(
    phases: AllocationPhase[],
    allocationStartDateTime: DateTime,
    timelineStartDateTime: DateTime,
    timelineEndDateTime: DateTime,
    phaseTypes: AllocationUsage[],
): PhaseGroup[] {
    let currentStartDateTime = allocationStartDateTime;

    const phaseGroups = phases.map((phase) => {
        const startDateTime = currentStartDateTime;
        const endDateTime = startDateTime.plus({ days: phase.cellSpan });
        currentStartDateTime = endDateTime;

        return {
            id: phase.id,
            startDateTime,
            endDateTime,
            temporaryCellSpanBefore: 0,
            confirmedCellSpan: phase.cellSpan,
            confirmedVisibleCellSpan: calculateConfirmedVisibleCellSpan(
                startDateTime,
                endDateTime,
                timelineStartDateTime,
                timelineEndDateTime,
            ),
            temporaryCellSpanAfter: 0,
            position: phase.position,
            temporarySpecialization: phase.specialization, // Initial value
            confirmedSpecialization: phase.specialization,
            hasTemporarySpecialization: false,
            isChangeTypePopupOpen: false,
            isLeftEndPosition: false, // Will be calculated correctly afterward
            isRightEndPosition: false, // Will be calculated correctly afterward
            insertPhaseBefore: null,
            insertPhaseAfter: null,
            color: phase.color,
            usage: {
                id: phase.usage.id,
                type: phase.usage.type,
                position: phase.usage.position,
            },
        };
    });

    return calculateAllocationState(phaseGroups, phaseTypes, timelineStartDateTime, timelineEndDateTime);
}

export function calculateInsertPhaseNames(
    phaseGroups: PhaseGroup[],
    phaseTypes: AllocationUsage[],
    phaseTypePriorities?: number[],
): Pick<
    PhaseGroup,
    | 'id'
    | 'startDateTime'
    | 'endDateTime'
    | 'temporaryCellSpanBefore'
    | 'confirmedCellSpan'
    | 'confirmedVisibleCellSpan'
    | 'temporaryCellSpanAfter'
    | 'position'
    | 'temporarySpecialization'
    | 'confirmedSpecialization'
    | 'isChangeTypePopupOpen'
    | 'insertPhaseBefore'
    | 'insertPhaseAfter'
    | 'color'
    | 'usage'
>[] {
    const existingPositions = phaseGroups.map((g) => g.usage.position);
    return phaseGroups.map((phaseGroup) => ({
        ...phaseGroup,
        insertPhaseBefore:
            getPhaseTypeToInsert(
                existingPositions,
                phaseGroup.usage.position,
                'Before',
                phaseTypes,
                phaseTypePriorities,
            )?.name || null,
        insertPhaseAfter:
            getPhaseTypeToInsert(existingPositions, phaseGroup.usage.position, 'After', phaseTypes, phaseTypePriorities)
                ?.name || null,
    }));
}

// respects priority list of phase order, if provided, else returns nearest possible
// phase position
export function getPhaseTypeToInsert(
    existingPositions: number[],
    insertingPosition: number,
    position: 'Before' | 'After',
    phaseTypes: AllocationUsage[],
    phaseTypePriorities?: number[],
) {
    const usedPositions = new Set(existingPositions);
    const allPositions = phaseTypes.map((p) => p.position);
    const availablePositions = new Set(allPositions).difference(usedPositions);
    const sortedPositions = [...allPositions].sort((a, b) => a - b);
    const sortedUsed = [...usedPositions].sort((a, b) => a - b);
    const sortedAvailable = [...availablePositions].sort((a, b) => a - b);
    const executionPosition = phaseTypes.find((p) => p.isExecution)!.position;

    let phaseTypePosition = -1;

    const pickFromPossiblePositions = (possiblePositions: number[]): number => {
        if (possiblePositions.length === 0) return -1;

        if (phaseTypePriorities && phaseTypePriorities.length > 0) {
            const prioritized = phaseTypePriorities.find((p) => possiblePositions.includes(p));
            return prioritized ?? -1;
        }

        const nearestToCenter =
            insertingPosition <= executionPosition
                ? possiblePositions.findLast(() => true)
                : possiblePositions.find(() => true);
        return nearestToCenter ?? -1;
    };

    if (position === 'Before') {
        const leftAnchor = sortedUsed.findLast((p) => p < insertingPosition) ?? sortedPositions[0] - 1;
        const possiblePositions = sortedAvailable.filter((p) => p > leftAnchor && p < insertingPosition);
        phaseTypePosition = pickFromPossiblePositions(possiblePositions);
    } else {
        const rightAnchor =
            sortedUsed.find((p) => p > insertingPosition) ?? sortedPositions[sortedPositions.length - 1] + 1;
        const possiblePositions = sortedAvailable.filter((p) => p > insertingPosition && p < rightAnchor);
        phaseTypePosition = pickFromPossiblePositions(possiblePositions);
    }

    return phaseTypes.find((t) => t.position === phaseTypePosition) ?? null;
}

export function isMainPhasePresent(
    phaseGroups: Pick<
        PhaseGroup,
        'position' | 'temporaryCellSpanBefore' | 'confirmedCellSpan' | 'temporaryCellSpanAfter'
    >[],
): boolean {
    return phaseGroups.some(
        (phaseGroup) =>
            phaseGroup.position === 'Main' &&
            phaseGroup.temporaryCellSpanBefore + phaseGroup.confirmedCellSpan + phaseGroup.temporaryCellSpanAfter > 0,
    );
}

export function calculateAllocationState(
    phaseGroups: PhaseGroup[],
    phaseTypes: AllocationUsage[],
    timelineStartDateTime: DateTime,
    timelineEndDateTime: DateTime,
    phaseTypePriorities?: number[],
): PhaseGroup[] {
    // Preserve existing phases and their positions
    const nonZeroLengthPhaseGroups = phaseGroups.filter(
        (phaseGroup) =>
            phaseGroup.temporaryCellSpanBefore > 0 ||
            phaseGroup.confirmedCellSpan > 0 ||
            phaseGroup.temporaryCellSpanAfter > 0,
    );

    // Ensure position is maintained during update
    const phaseGroupsWithPositions = nonZeroLengthPhaseGroups.map((phaseGroup) => ({
        ...phaseGroup,
        position: phaseGroup.position,
        confirmedVisibleCellSpan: calculateConfirmedVisibleCellSpan(
            phaseGroup.startDateTime,
            phaseGroup.startDateTime.plus({ days: phaseGroup.confirmedCellSpan }),
            timelineStartDateTime,
            timelineEndDateTime,
        ),
    }));

    const phaseGroupsWithButtons = calculateInsertPhaseNames(phaseGroupsWithPositions, phaseTypes, phaseTypePriorities);

    return phaseGroupsWithButtons.map((phaseGroup, index) => ({
        ...phaseGroup,
        isLeftEndPosition: index === 0,
        isRightEndPosition: index === phaseGroupsWithButtons.length - 1,
        id: phaseGroup.id ?? `temporary-new-phase-${nanoid()}`,
    }));
}

export function createStateMachine(
    phases: AllocationPhase[],
    isAllocationOptional: boolean,
    /**
     * Whether a click may activate the bar at all. A getter rather than a value: it depends on the rest of
     * the timeline, which changes while the allocation stays mounted.
     */
    isActivatable: () => boolean,
    onDeleteAllocation: () => Promise<void>,
    onUpdateAllocation: (
        phases: AllocationPhaseUpdate[],
        isAllocationOptional: boolean,
        referenceAllocationId: string | null,
    ) => Promise<{ phaseUpdates: AllocationPhaseUpdate[]; isOptional: boolean; referenceAllocationId: string | null }>,
    onReferenceAllocationIdChanged: (referenceAllocationId: string | null) => void,
    allocationStartDateTime: DateTime,
    timelineStartDateTime: DateTime,
    timelineEndDateTime: DateTime,
    onToggleEditPopup: (isOpen: boolean) => void,
    onDiscardLocationCheckboxChanges: () => void,
    onHideLocationCheckboxes: () => void,
    allocationId: string,
    referenceAllocationId: string | null,
    isAllocationRegistered: (allocationId: string) => boolean,
    phaseTypes: AllocationUsage[],
    phaseTypePriorities?: number[],
) {
    const phaseGroups = enrichPhases(
        phases,
        allocationStartDateTime,
        timelineStartDateTime,
        timelineEndDateTime,
        phaseTypes,
    );

    return setup({
        types: {
            context: {} as {
                phaseGroups: PhaseGroup[];
                timelineStartDateTime: DateTime;
                timelineEndDateTime: DateTime;
                allocationForm: 'Start-End' | 'Start-Middle' | 'Middle' | 'Middle-End';
                isInitialLocationSelectionChanged: boolean;
                checkedLocationIds: string[];
                isAllocationOptional: {
                    confirmed: boolean;
                    temporary: boolean;
                };
                referenceAllocation: {
                    confirmedId: string | null;
                    temporaryId: string | null;
                };
                allocationId: string;
                isReferenceAllocationRegistered: boolean;
                // Bumped every time a dismiss attempt is refused because changes are unconfirmed. The view
                // watches it to replay the review area's flash; a counter rather than a flag so consecutive
                // refusals are distinguishable.
                refusedDismissCount: number;
            },

            events: {} as
                | { type: 'Activate popup' }
                | { type: 'Inactivate popup' }
                | { type: 'Open popup' }
                | { type: 'Close popup' }
                | { type: 'Press escape' }
                | { type: 'Change start and or end'; startDateTime: DateTime; endDateTime: DateTime }
                | { type: 'Click edit entire allocation' }
                | { type: 'Click tab edit phases' }
                | { type: 'Click tab move date' }
                | { type: 'Delete allocation' }
                | { type: 'Abort deleting allocation' }
                | { type: 'Confirm deleting allocation' }
                | { type: 'Retry deleting allocation' }
                | { type: 'Deleting allocation pending' }
                | { type: 'Deleting allocation succeeded' }
                | { type: 'Deleting allocation failed' }
                | { type: 'Blur' }
                | {
                      type: 'Insert phase';
                      position: 'Before' | 'After';
                      insertingPosition: number;
                  }
                | {
                      type: 'Increase phase length';
                      position: 'Before' | 'After';
                      phaseGroupType: PhaseTypeDatabase;
                  }
                | {
                      type: 'Decrease phase length';
                      position: 'Before' | 'After';
                      phaseGroupType: PhaseTypeDatabase;
                  }
                | { type: 'Discard changes' }
                | { type: 'Confirm changes' }
                | { type: 'Retry changes' }
                | { type: 'Changes pending' }
                | { type: 'Changes succeeded' }
                | { type: 'Changes failed' }
                | { type: 'Location selection changed'; countOtherLocations: number }
                | { type: 'Show change type popup'; phaseGroupType: PhaseTypeDatabase }
                | { type: 'Hide change type popup' }
                | {
                      type: 'Change specialization type';
                      specialization: SpecializationType | null;
                      phaseGroupId: string;
                  }
                | { type: 'Toggle allocation optional' }
                | { type: 'Promote to reference allocation'; newReferenceAllocationId: string }
                | { type: 'Demote to normal allocation' }
                | { type: 'Update reference allocation registration'; isReferenceAllocationRegistered: boolean }
                | { type: 'Update reference allocation id'; newReferenceAllocationId: string | null }
                | {
                      type: 'Update phases';
                      phases: AllocationPhase[];
                      allocationStartDateTime: DateTime;
                  },
        },
        actors: {
            updateAllocation: fromPromise(
                async ({
                    input,
                }: {
                    input: {
                        phases: AllocationPhaseUpdate[];
                        isAllocationOptional: boolean;
                        referenceAllocationId: string | null;
                    };
                }) => {
                    return await onUpdateAllocation(
                        input.phases,
                        input.isAllocationOptional,
                        input.referenceAllocationId,
                    );
                },
            ),
            deleteAllocation: fromPromise(async () => {
                return onDeleteAllocation();
            }),
        },
        actions: {
            insertPhase: assign(({ context }, params: { position: 'Before' | 'After'; insertingPosition: number }) => {
                const phaseType = getPhaseTypeToInsert(
                    context.phaseGroups.map((p) => p.usage.position),
                    params.insertingPosition,
                    params.position,
                    phaseTypes,
                    phaseTypePriorities,
                );
                if (!phaseType) {
                    throw new Error('Tried to find the most likely phase type but there was none');
                }

                const executionPhase = phaseTypes.find((p) => p.isExecution);

                if (!executionPhase) {
                    throw new Error('Tried to find the execution phase type but there was none');
                }

                const referencePhaseGroup = context.phaseGroups.find(
                    (g) => g.usage.position === params.insertingPosition,
                );
                if (!referencePhaseGroup) {
                    throw new Error('Could not find phaseGroup in context.phaseGroups');
                }

                const executionPosition = executionPhase.position;
                const isRightOfExecution = params.insertingPosition > executionPosition;

                const startDateTime =
                    params.position === 'Before'
                        ? referencePhaseGroup.startDateTime.minus({ days: 1 })
                        : referencePhaseGroup.startDateTime.plus({
                              days: referencePhaseGroup.confirmedCellSpan,
                          });

                // Beware: the temporary phase positioning is only related to temporaryCellSpanBefore
                // temporaryCellSpanAfter and the correct position string ('Before-Main' | 'After-Main')
                // calculating the startDateTime or using the confirmedCellSpan seems to be irrelevant.
                // Perhaps we should make this more clear and or remove obsolete calculations entirely
                // to make this code easier to read and maintain

                const phaseToInsert: PhaseGroup = {
                    id: `temporary-new-phase-${nanoid()}`,
                    startDateTime,
                    endDateTime: startDateTime.plus({ days: 1 }),
                    temporaryCellSpanBefore: params.position === 'Before' && !isRightOfExecution ? 1 : 0,
                    temporaryCellSpanAfter:
                        params.position === 'After' || (params.position === 'Before' && isRightOfExecution) ? 1 : 0,
                    confirmedCellSpan: 0,
                    confirmedVisibleCellSpan: 0, // Will be calculated correctly afterwards
                    isLeftEndPosition: false, // Will be calculated correctly afterwards
                    isRightEndPosition: false, // Will be calculated correctly afterwards
                    insertPhaseBefore: null, // Will be calculated correctly afterwards
                    insertPhaseAfter: null, // Will be calculated correctly afterwards
                    position: params.position === 'Before' && !isRightOfExecution ? 'Before-Main' : 'After-Main',
                    temporarySpecialization: null,
                    confirmedSpecialization: null,
                    isChangeTypePopupOpen: false,
                    color: phaseType.color,
                    usage: {
                        id: phaseType.id,
                        type: phaseType.type,
                        position: phaseType.position,
                    },
                };

                const updatedPhaseGroups = [...context.phaseGroups, phaseToInsert].sort(
                    (a, b) => a.usage.position - b.usage.position,
                );

                return {
                    phaseGroups: calculateAllocationState(
                        updatedPhaseGroups,
                        phaseTypes,
                        context.timelineStartDateTime,
                        context.timelineEndDateTime,
                        phaseTypePriorities,
                    ),
                    allocationForm: calculateAllocationForm(
                        updatedPhaseGroups,
                        context.timelineStartDateTime,
                        context.timelineEndDateTime,
                    ),
                };
            }),

            increasePhaseLength: assign(
                ({ context }, params: { position: 'Before' | 'After'; phaseGroupType: PhaseTypeDatabase }) => {
                    const phaseGroups = context.phaseGroups.map((phaseGroup) => {
                        if (phaseGroup.usage.type === params.phaseGroupType) {
                            return {
                                ...phaseGroup,
                                temporaryCellSpanBefore:
                                    params.position === 'Before'
                                        ? phaseGroup.temporaryCellSpanBefore + 1
                                        : phaseGroup.temporaryCellSpanBefore,
                                temporaryCellSpanAfter:
                                    params.position === 'After'
                                        ? phaseGroup.temporaryCellSpanAfter + 1
                                        : phaseGroup.temporaryCellSpanAfter,
                            };
                        }

                        return phaseGroup;
                    });

                    return {
                        phaseGroups: calculateAllocationState(
                            phaseGroups,
                            phaseTypes,
                            context.timelineStartDateTime,
                            context.timelineEndDateTime,
                            phaseTypePriorities,
                        ),
                        allocationForm: calculateAllocationForm(
                            phaseGroups,
                            context.timelineStartDateTime,
                            context.timelineEndDateTime,
                        ),
                    };
                },
            ),

            decreasePhaseLength: assign(
                ({ context }, params: { position: 'Before' | 'After'; phaseGroupType: PhaseTypeDatabase }) => {
                    const phaseGroups = context.phaseGroups.map((phaseGroup) => {
                        if (phaseGroup.usage.type === params.phaseGroupType) {
                            const countCellSpan =
                                phaseGroup.temporaryCellSpanBefore +
                                phaseGroup.confirmedCellSpan +
                                phaseGroup.temporaryCellSpanAfter;

                            return {
                                ...phaseGroup,
                                temporaryCellSpanBefore:
                                    params.position === 'Before' && countCellSpan >= 1
                                        ? phaseGroup.temporaryCellSpanBefore - 1
                                        : phaseGroup.temporaryCellSpanBefore,
                                temporaryCellSpanAfter:
                                    params.position === 'After' && countCellSpan >= 1
                                        ? phaseGroup.temporaryCellSpanAfter - 1
                                        : phaseGroup.temporaryCellSpanAfter,
                            };
                        }

                        return phaseGroup;
                    });

                    return {
                        phaseGroups: calculateAllocationState(
                            phaseGroups,
                            phaseTypes,
                            context.timelineStartDateTime,
                            context.timelineEndDateTime,
                            phaseTypePriorities,
                        ),
                        allocationForm: calculateAllocationForm(
                            phaseGroups,
                            context.timelineStartDateTime,
                            context.timelineEndDateTime,
                        ),
                    };
                },
            ),

            discardChanges: assign(({ context }) => {
                onDiscardLocationCheckboxChanges();

                const phaseGroups = context.phaseGroups.map((phaseGroup) => {
                    return {
                        ...phaseGroup,
                        temporaryCellSpanBefore: 0,
                        temporaryCellSpanAfter: 0,
                        temporarySpecialization: phaseGroup.confirmedSpecialization,
                    };
                });

                return {
                    phaseGroups: calculateAllocationState(
                        phaseGroups,
                        phaseTypes,
                        context.timelineStartDateTime,
                        context.timelineEndDateTime,
                        phaseTypePriorities,
                    ),
                    allocationForm: calculateAllocationForm(
                        phaseGroups,
                        context.timelineStartDateTime,
                        context.timelineEndDateTime,
                    ),
                    isAllocationOptional: {
                        ...context.isAllocationOptional,
                        temporary: context.isAllocationOptional.confirmed,
                    },
                    referenceAllocation: {
                        ...context.referenceAllocation,
                        temporaryId: context.referenceAllocation.confirmedId,
                    },
                };
            }),

            updateLocationSelectionState: assign((_, params: { countOtherLocations: number }) => {
                return { isInitialLocationSelectionChanged: params.countOtherLocations > 0 };
            }),

            openChangeTypePopup: assign(({ context }, params: { phaseGroupType: PhaseTypeDatabase }) => {
                const phaseGroups = context.phaseGroups.map((phaseGroup) => {
                    return {
                        ...phaseGroup,
                        isChangeTypePopupOpen: phaseGroup.usage.type === params.phaseGroupType,
                    };
                });

                return { phaseGroups };
            }),

            hideChangeTypePopup: assign(({ context }) => {
                const phaseGroups = context.phaseGroups.map((phaseGroup) => {
                    return {
                        ...phaseGroup,
                        isChangeTypePopupOpen: false,
                    };
                });

                return { phaseGroups };
            }),

            changeSpecializationTemporarily: assign(
                ({ context }, params: { phaseGroupId: string; specialization: SpecializationType | null }) => {
                    const phaseGroups = context.phaseGroups.map((phaseGroup) => {
                        if (phaseGroup.id === params.phaseGroupId) {
                            return {
                                ...phaseGroup,
                                temporarySpecialization: params.specialization,
                                hasTemporarySpecialization: true,
                                isChangeTypePopupOpen: false,
                            };
                        }

                        return phaseGroup;
                    });

                    return { phaseGroups };
                },
            ),

            updateStartAndOrEnd: assign(({ context }, params: { startDateTime: DateTime; endDateTime: DateTime }) => {
                const phaseGroups = calculateAllocationState(
                    context.phaseGroups,
                    phaseTypes,
                    params.startDateTime,
                    params.endDateTime,
                    phaseTypePriorities,
                );
                return {
                    phaseGroups,
                    timelineStartDateTime: params.startDateTime,
                    timelineEndDateTime: params.endDateTime,
                };
            }),

            toggleAllocationOptional: assign(({ context }) => {
                return {
                    isAllocationOptional: {
                        ...context.isAllocationOptional,
                        temporary: !context.isAllocationOptional.temporary,
                    },
                };
            }),

            updateReferenceAllocationRegistration: assign((_, params: { isReferenceAllocationRegistered: boolean }) => {
                return {
                    isReferenceAllocationRegistered: params.isReferenceAllocationRegistered,
                };
            }),

            refuseDismiss: assign(({ context }) => {
                return { refusedDismissCount: context.refusedDismissCount + 1 };
            }),
        },
        guards: {
            isAllocationActivatable() {
                return isActivatable();
            },
            shouldReviewAreaBeOpen({ context }) {
                const hasPhaseChanges = context.phaseGroups.some(
                    (phaseGroup) => phaseGroup.temporaryCellSpanBefore !== 0 || phaseGroup.temporaryCellSpanAfter !== 0,
                );

                const hasTemporarySpecializationChanges = context.phaseGroups.some(
                    (phaseGroup) => phaseGroup.confirmedSpecialization !== phaseGroup.temporarySpecialization,
                );

                const isAllocationOptionalChanged =
                    context.isAllocationOptional.confirmed !== context.isAllocationOptional.temporary;

                const isReferenceAllocationChanged =
                    context.referenceAllocation.confirmedId !== context.referenceAllocation.temporaryId;

                return (
                    hasPhaseChanges ||
                    hasTemporarySpecializationChanges ||
                    context.isInitialLocationSelectionChanged ||
                    isAllocationOptionalChanged ||
                    isReferenceAllocationChanged
                );
            },
            shouldReviewAreaBeClosed({ context }) {
                const hasPhaseChanges = context.phaseGroups.some(
                    (phaseGroup) => phaseGroup.temporaryCellSpanBefore !== 0 || phaseGroup.temporaryCellSpanAfter !== 0,
                );

                const hasTemporarySpecializationChanges = context.phaseGroups.some(
                    (phaseGroup) => phaseGroup.confirmedSpecialization !== phaseGroup.temporarySpecialization,
                );

                const isAllocationOptionalChanged =
                    context.isAllocationOptional.confirmed !== context.isAllocationOptional.temporary;

                const isReferenceAllocationChanged =
                    context.referenceAllocation.confirmedId !== context.referenceAllocation.temporaryId;

                return (
                    !hasPhaseChanges &&
                    !hasTemporarySpecializationChanges &&
                    !context.isInitialLocationSelectionChanged &&
                    !isAllocationOptionalChanged &&
                    !isReferenceAllocationChanged
                );
            },
            isChangeTypePopupOpen({ context }) {
                return context.phaseGroups.some((phaseGroup) => phaseGroup.isChangeTypePopupOpen);
            },
            isChangeTypePopupClosed({ context }) {
                return context.phaseGroups.every((phaseGroup) => !phaseGroup.isChangeTypePopupOpen);
            },
            isValidForSave({ context }) {
                return isMainPhasePresent(context.phaseGroups);
            },
        },
    }).createMachine({
        /** @xstate-layout N4IgpgJg5mDOIC5QEEA2qD2BjAhgFwEsMA7AYgGEALHYmAAljxwCc86J8w7CBbMAbQAMAXUSgADhlgFCJMSAAeiAMwBGAEwBWAHQBOACybl6-QDYtADnWrlAGhABPRKtMB2ba42r9nzYIuegsoAvsH2aJi4smRUNPRgxBDsnNwEfEKiSCCS0tHySghq6rramqoGuspVqq7qWvZOhe6urha66p76FoKCnaHh6Nj4RMTa5JiwkKTIWIQAbimS4gCu4hnyOTIj+YjqpqaC2lo9XpbWdo7OVfraVsXKvWaauqYhYSARQ9HaM-OcEKQAPLiBJ0JardZZTZ5LIFDQNZzqfofQZREbaACiEBkYIwK3EdDxCVIEBIYG0jE4P1RwxImOxbHBBKJxEhEikWzksN2yhe2kElVcvN5glMhlMCIQNn0hxlrw0dWU5k0rmRnzRdKxOKZhJBMQmXCZbOyHJhoAK6l5pn5guFulF4slGjaelFlte+n0vKFapp3y1jLxq11CW0ABUcAAjOjEHBzAhQWlkUnEcmUvDk9VJ+naoPMvXhqMxuMJpPG6HbbkIS18gXKIWVe1izQSy5SkzqbRyy1WAyqFW+yLZgO4-Eh0YR6Ox+OJ-0MsHUSawCioAhYADW3CLPAwcy4HAz5dNlfNzgs+lUXfPVTcpgs9a9TsE1iOd867WUZiR7yzc9zY5ZQspxLWd0RHcRFzgFc103SAcQSQhmC4HA-RGI9chPRRnBeS81FcUw-AI8pdFcSUyh0e1-F0e8tA6FxBy+MD5x1QDJ2LGdh2YyDlwASWISZWAXHBJnQzliB2KVdBI-khWMXpNBMHx1DI15XSomiFM8UwGI1UZwLzccgPY0s-0ZbjSD4rAkOEw1ILoVAEigPBKFEs0sIQXQNFKVp+zaMpXC9TRJX0XQdHUIINF5doLACHTOP-YNWKLacTKY7VzIAETAKywBsoTJnsxznNczCCntEob2fA4ejMfxJV5CwPBC0KqgsIwvDi0zR0Sgs2JS0DNS4mzlwAGUYkgGDABzZhGOgsGoWhIBKrlT0k0xcPIgV7RCvZW0acKWj0DRDC00wSO0n9UMGhL81DPqQPiszhtIABlSgMAAdzmhb6DwBwQW6tYRA2Y8Vvc8p1u0ZRNqkwQdv2SVrAsa0QsETQrBcM78M6tLAwA3rkoerqIOegAJAgIC4ea4i4P6AaNYGoVB8Sqwhjb+y2uH2gRts6lC-k316THzpx668Z6u7CY44nuO0AAlMB4zAL6WFy+XFYIZW6FVnBxxJMkKSYDNqSHYmDKS4Dpdx-K4HVpWVesu3NYd3Lx2WlnQEafZDjRnxNPPFpXCC8HkZ0aGOdh+GLoGU3rZYgnLdSsWbdgJ2tZ1tOXd1llSAyghYFwZgkhJgrqcW2B3Yki9OyMcqugOFVXBeSVuncftzBaci1Dh0W9OY82E+Mga+-S4bM+1x2FftifXZzyuqx8EpPSMcU2qMAJSLbfDL3b4oahbM6jF7nNxduicpaTkenqXceM6n52Z+zgssoAMxwZZUDwCgSBfghmB4G230aYV0ZuyDCYMCitGtB0T8qgahFEtJvRoAQbivGKHDNe7Q0bH30vjSWidh4nxTrfSeGt07WUMgABTsqsA8hoEjYloPrVM2gCDEDmBgdc5ISAAFVxB0OocNeeq0bDQy7PhIwLZW41CQYgIObcWx7FqBYDQQd9A4P7ng8+BDHrEPvuQtW+is5UJofwxYDC2FQFIGAZgzAMDMG0OIVA+AX72J4NoXhZiMyCKXMI8GVQdA+AItDO8QsWiSnkdoXebh1AqI6JodRl1Y7J3jvgoeuiS62yMY-Ehs8CwKzwMwBwgCy4wFgHQWhnBSAFKKSUn6cA-Fwg7HoCRB07xeleC3NGUTFExLiWojRN1DL3Stik2W2SM7jCkFMRpiBq6lBFAYZGaMWjNzbPaZQ2gDiNi6C0bwTdBmn2GRfQh4FZZZQchmHJFywBXJ1nrZAkZ7FsEppcyx2srqslASacBHtwZdEvL0B4F41BBL2E6feWyFRPHRgk5QFhDmA2OTomWY8bl3Mdui5CFDAKv3fp-b+xBf7-3YFNW57yULJK+ZkMBYkq4EX5CFA4+wHgvA6E6V47h0YBHhb0Ewz5EWpO0ek1FN8sXXLJRivJoZ0UUs+WCCxTCUzkjYRwrhJtxpXyRRbEVcdzmSuxWrcV9zcWSrlVShViRLEIFVZqjIsyEAqmtHAqS9cFIqPCW2fs9p+QxVqG4VQfKfRJM1UQoVRl+oZP1Zcw1OBtDGpxc-M1tAPkWr1IwqxNi7EOKcS4txGrdJhoHmkyNorbYJqNQax+hlZUpspZqy1GabXsLtSIB11FAUXiboG6GiiLActUI1da1gg6fjRpaRJMdQ24IlsK0teq0VVozhWp+oYanFNeeSutnzqm3NqZuwg26qXtpUYymonkgiaD7U6KRUSFRo1Ch0VwAqQ2FpnWfCNRMF1iqXY7KZkwATGs+Q67wp6gVwxsPs8we1ER7DvXUfQWgzouHRoK4tc6v1jLHrERa3B-qGjzGMepeH6boYoMR2AIIsAEBQgQAAXkmEjAhvkVjBo0DQ-hGWhXwuYL8Kj7AFCqJspsWg0NaM-aMrVmTU44d+vhwGRGaZMe1XqUgDqhOumVBCg4XGnW8fMCosTs6JOXzDbLWTtN5NMjGAaAEDrwqbLfMHdjHRDgJO4-sEwBnVBGY-SM0zABZXc+4qnjBgnQOCbAEJ-2QsBljzN5DIN6Fs59MUDDuf8BcC0agl7mEtP2dqWNfPIt1XSILe5kgZmghuLc0ZIspxA+eS815ZJ1DqComDCAm4lH8EHLQ-qTq6GKzq+dydotIVTZq6rm4mB1aGr4+LvyJLhRuM8PyxgfC9CCE6ZG1pzqjvRkHQUw3B6ja1eN2LVLpu1boDuCrdD7Nw1KNRFqJhn3PouOx6KHg2haGhgEZ49ZQjvGIBgSm8Asi-kwqxv5BQAC0-LJRw6DlDPmcDwpSJIgi192Z-2QBBktqsWg-BXkMM+Z8SHlJevQV2N0fl7TSlVDj74vwCALAzBAAndKqwkURk8AWtEEno1if2E7CQuduXh+UKn+0TBRIg6yhsxgBzM7juhkzw8JelUQHD7w+h6pwK7K5xD568tVDFxhyTZnhpa4gTrtZjQQo6EHaOpUYoMcGAtxrqNY8JnWVt7D3YnoPBCh8FYAiLX+1tmF6UQNnp64vdQ6rlJ6v-OnPm1kshWdcmrr+TDiS0GfvHRileqq+u2z7BKPeAJBwg6wujiiKlRbxNp59zfP3his-VtNW-D+eAA8F4CkXkwJe-B7HL8gtwumeNedid+Kdb7NHGdb2W1OHe43r5MXlSpVz02WIH6zZ8l5FE+DUNIz1yD4VHBcC4GUg6zrQy9yv79mfp53y7ya-Je7imZKAeXCpXiYAB+IihgjU6WWgkc3MnWAQnYXMems+VgT+Jyber+D8kytmwB7kxQnYLQn4rQew6MXoUejQ+EPWRQrQJE+EDw8+je06S+fmyBq+8av6uUmBFoKopQKoXakGbQbgT4MURwbQAS0MsSCSSBKKL+qcK6zBMa3eeobBKg3SV6AUNQPBLwsiUoXQhwFBW01EQcNQk6tBi+QyI2mGUm0atysaMhlhchMqYAven8ChhQRgnBKhngZ+6hHKDw-IaM8KEi68DeUOKeLejBkh1hUqcaK6NayaUAk2ukja++TMhOq0tQmyQh0MPG3gvOXqBwO8tQ-g-YMo7QSeC+GSqeoRWGP6shy6LBue6shSG6MRcRSYTh96rhF4xRSG5QXhOgPQ68-h8KgRnyzey+FR5hi61Rf6GBSR3OIi9YuEjwb24Uz454EKp6h8Dc541ETu4hpW4xN8FmymTIThcOXQT4tQHgewyM8KRg1guxZ21uBxxGdMBG+IimuGLxKm4uMxkuiAggTo4UjUcBM+fGPmyeUm5REhlRtshxnx1meOnOPx2uCA-xXqn4NcTuIJ3m9xZh2g5WIWGYTh4UnkghXcDw2ylQXS7gbg-gbQhgqM94OJVuI4F2zRbk+eVYcODuPIJJooAQQsle2MwOQAA */
        id: 'Allocation',
        initial: 'Closed',
        states: {
            Closed: {
                on: {
                    'Activate popup': {
                        guard: 'isAllocationActivatable',
                        target: 'Activated',
                    },
                },
            },

            Activated: {
                on: {
                    'Open popup': {
                        target: 'Edit popup open',
                        actions: () => onToggleEditPopup(true),
                    },
                    'Inactivate popup': {
                        target: 'Closed',
                    },
                },
            },

            'Edit popup open': {
                states: {
                    'Tab navigation': {
                        initial: 'Edit phases',
                        states: {
                            'Edit phases': {
                                type: 'parallel',

                                states: {
                                    'Change type popup': {
                                        initial: 'Closed',

                                        states: {
                                            'Change type popup open': {
                                                on: {
                                                    'Change specialization type': {
                                                        actions: [
                                                            {
                                                                type: 'changeSpecializationTemporarily',
                                                                params: ({ event }) => ({
                                                                    specialization: event.specialization,
                                                                    phaseGroupId: event.phaseGroupId,
                                                                }),
                                                            },
                                                        ],
                                                    },

                                                    'Press escape': {
                                                        actions: 'hideChangeTypePopup',
                                                    },
                                                },

                                                always: [
                                                    {
                                                        target: 'Closed',
                                                        guard: 'isChangeTypePopupClosed',
                                                    },
                                                ],
                                            },

                                            Closed: {
                                                always: [
                                                    {
                                                        target: 'Change type popup open',
                                                        guard: 'isChangeTypePopupOpen',
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },

                                on: {
                                    'Click tab move date': {
                                        target: 'Move date',
                                    },

                                    'Click edit entire allocation': {
                                        target: 'Edit entire allocation',
                                    },

                                    'Insert phase': {
                                        actions: {
                                            type: 'insertPhase',
                                            params: ({ event }) => ({
                                                position: event.position,
                                                insertingPosition: event.insertingPosition,
                                            }),
                                        },
                                    },

                                    'Increase phase length': {
                                        actions: {
                                            type: 'increasePhaseLength',
                                            params: ({ event }) => ({
                                                position: event.position,
                                                phaseGroupType: event.phaseGroupType,
                                            }),
                                        },
                                    },

                                    'Decrease phase length': {
                                        actions: {
                                            type: 'decreasePhaseLength',
                                            params: ({ event }) => ({
                                                position: event.position,
                                                phaseGroupType: event.phaseGroupType,
                                            }),
                                        },
                                    },

                                    'Show change type popup': {
                                        target: 'Edit phases',
                                        actions: [
                                            {
                                                type: 'openChangeTypePopup',
                                                params: ({ event }) => ({ phaseGroupType: event.phaseGroupType }),
                                            },
                                        ],
                                    },

                                    'Hide change type popup': {
                                        target: 'Edit phases',
                                        actions: 'hideChangeTypePopup',
                                    },
                                },
                            },

                            'Move date': {
                                on: {
                                    'Click edit entire allocation': {
                                        target: 'Edit entire allocation',
                                    },
                                    'Click tab edit phases': {
                                        target: 'Edit phases',
                                    },
                                },
                            },

                            'Edit entire allocation': {
                                on: {
                                    'Click tab edit phases': {
                                        target: 'Edit phases',
                                    },
                                    'Click tab move date': {
                                        target: 'Move date',
                                    },
                                    'Toggle allocation optional': {
                                        actions: 'toggleAllocationOptional',
                                    },
                                    'Promote to reference allocation': {
                                        actions: assign(({ context, event }) => {
                                            return {
                                                referenceAllocation: {
                                                    ...context.referenceAllocation,
                                                    temporaryId: event.newReferenceAllocationId,
                                                },
                                            };
                                        }),
                                    },
                                    'Demote to normal allocation': {
                                        actions: assign(({ context }) => {
                                            // Use Case: Is already reference allocation, should be demoted
                                            if (
                                                context.referenceAllocation.temporaryId ===
                                                context.referenceAllocation.confirmedId
                                            ) {
                                                return {
                                                    referenceAllocation: {
                                                        ...context.referenceAllocation,
                                                        temporaryId: null,
                                                    },
                                                };
                                            }

                                            // Use Case: Is only temporary reference allocation, should be reset
                                            return {
                                                referenceAllocation: {
                                                    ...context.referenceAllocation,
                                                    temporaryId: context.referenceAllocation.confirmedId,
                                                },
                                            };
                                        }),
                                    },
                                },
                            },
                        },
                        onDone: {
                            target: '#Allocation.Closed',
                        },
                    },

                    'Review area': {
                        initial: 'Closed',

                        states: {
                            'Review area open': {
                                initial: 'Default',

                                states: {
                                    Default: {
                                        on: {
                                            'Confirm changes': {
                                                guard: 'isValidForSave',
                                                target: 'Changes pending',
                                            },
                                        },

                                        always: [
                                            {
                                                target: '#Allocation.Edit popup open.Review area.Closed',
                                                guard: 'shouldReviewAreaBeClosed',
                                            },
                                        ],
                                    },

                                    'Changes pending': {
                                        invoke: {
                                            id: 'onUpdateAllocation',
                                            src: 'updateAllocation',
                                            input: ({ context }) => ({
                                                phases: context.phaseGroups.map((phaseGroup) => {
                                                    return {
                                                        ...phaseGroup,
                                                        cellSpanBefore: phaseGroup.temporaryCellSpanBefore,
                                                        cellSpan: phaseGroup.confirmedCellSpan,
                                                        cellSpanAfter: phaseGroup.temporaryCellSpanAfter,
                                                        position: phaseGroup.position,
                                                        specialization: phaseGroup.confirmedSpecialization,
                                                        newSpecialization: phaseGroup.temporarySpecialization,
                                                    };
                                                }),
                                                isAllocationOptional: context.isAllocationOptional.temporary,
                                                referenceAllocationId: context.referenceAllocation.temporaryId,
                                            }),

                                            onDone: {
                                                actions: [
                                                    assign(({ context, event }) => {
                                                        const { phaseUpdates, isOptional, referenceAllocationId } =
                                                            event.output;
                                                        const updatedPhaseGroups = phaseUpdates.map((phase) => {
                                                            return {
                                                                id: phase.id,
                                                                startDateTime: phase.startDateTime,
                                                                endDateTime: phase.endDateTime,
                                                                temporaryCellSpanBefore: phase.cellSpanBefore,
                                                                confirmedCellSpan: phase.cellSpan,
                                                                confirmedVisibleCellSpan: -1,
                                                                temporaryCellSpanAfter: phase.cellSpanAfter,
                                                                isLeftEndPosition: false, // Will be calculated correctly afterward
                                                                isRightEndPosition: false, // Will be calculated correctly afterward
                                                                insertPhaseBefore: null, // Will be calculated correctly afterward
                                                                insertPhaseAfter: null, // Will be calculated correctly afterward
                                                                position: phase.position,
                                                                temporarySpecialization: phase.newSpecialization,
                                                                confirmedSpecialization: phase.specialization,
                                                                isChangeTypePopupOpen: false,
                                                                color: phase.color,
                                                                usage: {
                                                                    id: phase.usage.id,
                                                                    type: phase.usage.type,
                                                                    position: phase.usage.position,
                                                                },
                                                            };
                                                        });

                                                        const phaseGroupsAllocationState = calculateAllocationState(
                                                            updatedPhaseGroups,
                                                            phaseTypes,
                                                            context.timelineStartDateTime,
                                                            context.timelineEndDateTime,
                                                            phaseTypePriorities,
                                                        );

                                                        onReferenceAllocationIdChanged(referenceAllocationId);
                                                        onHideLocationCheckboxes();

                                                        return {
                                                            phaseGroups: phaseGroupsAllocationState,
                                                            isAllocationOptional: {
                                                                confirmed: isOptional,
                                                                temporary: isOptional,
                                                            },
                                                            referenceAllocation: {
                                                                confirmedId: referenceAllocationId,
                                                                temporaryId: referenceAllocationId,
                                                            },
                                                            allocationForm: calculateAllocationForm(
                                                                phaseGroupsAllocationState,
                                                                context.timelineStartDateTime,
                                                                context.timelineEndDateTime,
                                                            ),
                                                        };
                                                    }),
                                                ],
                                                target: '#Allocation.Closed',
                                            },

                                            onError: {
                                                target: 'Retry changes update',
                                                actions: ({ event }: { event: { error: unknown } }) => {
                                                    console.error('Update allocation failed:', event.error);
                                                },
                                            },
                                        },
                                    },

                                    'Retry changes update': {
                                        on: {
                                            'Retry changes': 'Changes pending',
                                        },
                                    },
                                },

                                on: {
                                    'Discard changes': {
                                        target: 'Closed',
                                        actions: 'discardChanges',
                                    },

                                    /**
                                     * Unconfirmed changes are not visible on the closed allocation bar, so letting a
                                     * dismiss through would strand them somewhere in the timeline. Both gestures are
                                     * refused here instead — swallowing the event keeps the popup open — and the view
                                     * flashes the review area so the refusal is not read as an unresponsive key.
                                     * Leaving requires Discard or Confirm.
                                     */
                                    'Press escape': {
                                        actions: 'refuseDismiss',
                                    },

                                    'Close popup': {
                                        actions: 'refuseDismiss',
                                    },
                                },

                                onDone: {
                                    target: '#Allocation.Closed',
                                },
                            },

                            Closed: {
                                always: [
                                    {
                                        target: 'Review area open',
                                        guard: 'shouldReviewAreaBeOpen',
                                    },
                                ],
                            },
                        },
                    },

                    'Delete area': {
                        initial: 'Closed',

                        states: {
                            'Delete area open': {
                                initial: 'Default',

                                states: {
                                    Default: {
                                        on: {
                                            'Confirm deleting allocation': 'Deleting allocation pending',
                                        },
                                    },

                                    'Deleting allocation pending': {
                                        invoke: {
                                            src: 'deleteAllocation',

                                            onDone: {
                                                // Selection mode is ended reliably by the delete provider in
                                                // Timeline.vue (which outlives this component), not here — an
                                                // action on this transition races the component's unmount.
                                                target: '#Allocation.Closed',
                                            },

                                            onError: {
                                                target: 'Retry deleting allocation',
                                                actions: ({ event }: { event: { error: unknown } }) => {
                                                    console.error('Delete allocation failed:', event.error);
                                                },
                                            },
                                        },
                                    },

                                    'Retry deleting allocation': {
                                        on: {
                                            'Retry deleting allocation': 'Deleting allocation pending',
                                        },
                                    },
                                },

                                on: {
                                    'Abort deleting allocation': {
                                        target: 'Closed',
                                    },

                                    // Deliberately not scoped to 'Default': Escape while the deletion is in
                                    // flight must abort the confirmation rather than fall through and close
                                    // the whole popup.
                                    'Press escape': {
                                        target: 'Closed',
                                    },
                                },
                            },

                            Closed: {
                                on: {
                                    'Delete allocation': {
                                        target: 'Delete area open',
                                    },
                                },
                            },
                        },
                    },
                },

                type: 'parallel',

                on: {
                    'Close popup': {
                        target: 'Closed',
                        actions: () => onToggleEditPopup(false),
                    },

                    /**
                     * The fallback for Escape. Any nested state that handles 'Press escape' swallows the event
                     * before it gets here, so Escape dismisses the innermost thing that is open — XState only
                     * consults a parallel state's own handlers when none of its regions produced a transition.
                     */
                    'Press escape': {
                        target: 'Closed',
                        actions: () => onToggleEditPopup(false),
                    },
                },
            },
        },
        on: {
            'Change start and or end': {
                actions: [
                    {
                        type: 'updateStartAndOrEnd',
                        params: ({ event }) => ({
                            startDateTime: event.startDateTime,
                            endDateTime: event.endDateTime,
                        }),
                    },
                ],
            },
            /**
             * Driven by the copy-to-locations selection, which lives outside this allocation entirely — the
             * checkboxes are on the first-column rows. It therefore belongs here at the root, like the other
             * externally-driven events: handled in a single tab's state it was silently dropped whenever the
             * popup sat on another tab, leaving `isInitialLocationSelectionChanged` false so the review area
             * never opened and confirming a copy did nothing at all.
             */
            'Location selection changed': {
                actions: [
                    {
                        type: 'updateLocationSelectionState',
                        params: ({ event }) => ({
                            countOtherLocations: event.countOtherLocations,
                        }),
                    },
                ],
            },
            'Update reference allocation registration': {
                actions: [
                    {
                        type: 'updateReferenceAllocationRegistration',
                        params: ({ event }) => ({
                            isReferenceAllocationRegistered: event.isReferenceAllocationRegistered,
                        }),
                    },
                ],
            },
            'Update reference allocation id': {
                actions: assign(({ event }) => {
                    return {
                        referenceAllocation: {
                            temporaryId: event.newReferenceAllocationId,
                            confirmedId: event.newReferenceAllocationId,
                        },
                    };
                }),
            },
            'Update phases': {
                actions: assign(({ context, event }) => {
                    const phaseGroups = enrichPhases(
                        event.phases,
                        event.allocationStartDateTime,
                        context.timelineStartDateTime,
                        context.timelineEndDateTime,
                        phaseTypes,
                    );
                    return {
                        phaseGroups,
                        allocationForm: calculateAllocationForm(
                            phaseGroups,
                            context.timelineStartDateTime,
                            context.timelineEndDateTime,
                        ),
                    };
                }),
            },
        },
        context: {
            phaseGroups,
            timelineStartDateTime,
            timelineEndDateTime,
            allocationForm: calculateAllocationForm(phaseGroups, timelineStartDateTime, timelineEndDateTime),
            isInitialLocationSelectionChanged: false,
            checkedLocationIds: [],
            isAllocationOptional: {
                confirmed: isAllocationOptional,
                temporary: isAllocationOptional,
            },
            allocationId,
            referenceAllocation: {
                confirmedId: referenceAllocationId,
                temporaryId: referenceAllocationId,
            },
            isReferenceAllocationRegistered:
                referenceAllocationId !== null ? isAllocationRegistered(referenceAllocationId) : false,
            refusedDismissCount: 0,
            phaseTypes,
        },
    });
}
